<script setup lang="ts">
// Create/edit drawer for everything on a node: the form of the entity's
// kind (forms/*.vue) inside the drawer, which owns what every form shares:
// the title, Save/Create, errors, the unsaved-changes guard (WEB-7) and the
// notice when someone else changed the entity meanwhile (WEB-8). A form
// exposes save() (throws what went wrong), snapshot() (its state, for the
// guard) and optionally noun (for the title) and saveLabel ("Create 20").
import { computed, onMounted, ref, watch, type Component } from 'vue'
import type { EditTarget } from '../api'
import { useNode, Cancelled } from '../store'
import Drawer from './Drawer.vue'
import DestinationForm from './forms/DestinationForm.vue'
import EncodeForm from './forms/EncodeForm.vue'
import GroupForm from './forms/GroupForm.vue'
import InputForm from './forms/InputForm.vue'
import NodeForm from './forms/NodeForm.vue'
import OutputForm from './forms/OutputForm.vue'
import StorageForm from './forms/StorageForm.vue'

const props = defineProps<{ target: EditTarget }>()
const emit = defineEmits<{ close: [] }>()
const node = useNode()
const cfg = computed(() => node.config!)
const t = props.target
/* eslint-disable @typescript-eslint/no-unsafe-assignment -- the linter cannot read .vue types; vue-tsc checks these */
const forms: Record<EditTarget['kind'], Component> = {
    node: NodeForm,
    input: InputForm,
    encode: EncodeForm,
    destination: DestinationForm,
    group: GroupForm,
    output: OutputForm,
    storage: StorageForm,
}
/* eslint-enable @typescript-eslint/no-unsafe-assignment */
const form = ref<{ save(): Promise<void>; snapshot(): string; noun?: string; saveLabel?: string } | null>(null)
const isNew = computed(() => t.kind !== 'node' && !t.id)
const title = computed(() =>
    t.kind === 'node' ? 'Node settings' : `${isNew.value ? 'New' : 'Edit'} ${form.value?.noun ?? t.kind}`,
)
const err = ref('')
const busy = ref(false)

// Unsaved changes: the drawer asks before it closes. The form as it stands
// once mounted (defaults filled in) is the baseline.
const baseline = ref('') // a ref: the computed must re-run once it is set
onMounted(() => (baseline.value = form.value?.snapshot() ?? ''))
const dirty = computed(() => baseline.value !== '' && form.value?.snapshot() !== baseline.value)

// Someone else changed this entity while it was open (the other operator's
// save, or the reload after a 409): saving the form as it is would undo
// their change without anyone noticing.
const lists = {
    input: 'inputs',
    encode: 'encodes',
    destination: 'destinations',
    group: 'groups',
    output: 'outputs',
    storage: 'storage',
} as const
const storedNow = () => {
    if (t.kind === 'node' || !t.id) return ''
    return JSON.stringify(
        (cfg.value[lists[t.kind]] as { id: string }[] | undefined)?.find((x) => x.id === t.id) ?? null,
    )
}
const openedAs = storedNow()
const changedBy = ref<string | null>(null)
watch(
    () => cfg.value.version,
    () => {
        if (busy.value || !openedAs || storedNow() === openedAs) return
        changedBy.value = [...node.events].reverse().find((e) => e.kind === 'config')?.actor || 'someone else'
    },
)

// a form's own check of what the node would refuse (WEB-37): Save waits
const problem = computed(() => (form.value as { problem?: () => string } | null)?.problem?.() ?? '')
async function save() {
    err.value = ''
    if (problem.value) {
        err.value = problem.value
        return
    }
    busy.value = true
    try {
        await form.value!.save()
        emit('close')
    } catch (e) {
        // not confirmed (ChangesDialog): the form stays open as it was
        if (!(e instanceof Cancelled)) err.value = e instanceof Error ? e.message : String(e)
    } finally {
        busy.value = false
    }
}
</script>

<template>
    <Drawer as="form" :title="title" :dirty="dirty" @submit.prevent="save" @close="emit('close')">
        <component :is="forms[t.kind]" ref="form" :target="t" @close="emit('close')" />
        <div v-if="changedBy" class="mt-2 text-warn" role="alert">
            This {{ t.kind }} was changed by {{ changedBy }} while you edited it. Cancel and open it again, or
            {{ isNew ? 'Create' : 'Save' }} to overwrite their change.
        </div>
        <div v-if="err" class="mt-2 text-bad" role="alert">{{ err }}</div>
        <template #footer
            ><button type="button" @click="emit('close')">Cancel</button
            ><button class="btn-primary" type="submit" :disabled="busy || !!problem">
                {{ form?.saveLabel ?? (isNew ? 'Create' : 'Save') }}
            </button></template
        >
    </Drawer>
</template>
